import React, { Dispatch, SetStateAction, useMemo, useContext, useEffect } from "react";
import { State, SelectVariant } from "./types";
import { cn } from "../../../utils/cn";
import { IoBatteryHalfOutline, IoWarningOutline } from "react-icons/io5";
import { GiBlackBook, GiSmartphone } from "react-icons/gi";
import { GoPackageDependents } from "react-icons/go";
import { TbMessageLanguage } from "react-icons/tb";
// @ts-ignore
import { CartContext } from "@components/contexts/CartContext";
import { toast } from "react-toastify";
import {
    MdOutlineLocalOffer,
    MdAddShoppingCart,
    MdInfoOutline,
    MdEuroSymbol,
} from "react-icons/md";
// @ts-ignore
import Image from "@components/common/CloudinaryImage";
import { FaEarthAsia } from "react-icons/fa6";
import useSound from "use-sound";
// @ts-ignore
import addToBasketSfx from "@static/sounds/addProduct.mp3";
import { Link } from "gatsby";

interface Props {
    state: State;
    setState: Dispatch<SetStateAction<State>>;
    item: any;
}

const Component = ({ state, setState, item }: Props) => {
    return (
        <article className={cn("space-y-5 relative flex flex-col justify-between")}>
            <div className={cn(styles.container)}>
                <div className={cn(styles.imageContainer)}>
                    <ItemImage item={item} />
                    {item.stock && (
                        <>
                            <ItemFloatingLowStock item={item} />
                            <ItemFloatingOffer item={item} />
                        </>
                    )}
                </div>

                <div className="p-5 w-full space-y-3">
                    <ItemTitle item={item} />
                    <ItemDescription item={item} />
                    <ItemStock item={item} />
                    <ItemVariants item={item} state={state} setState={setState} />
                    <ItemPrice item={item} state={state} />
                    <div className="flex items-center justify-between">
                        <ItemMultipleLangs item={item} />
                        <ItemRef id={item.id} />
                    </div>
                </div>
            </div>

            <ItemButtons item={item} state={state} />
        </article>
    );
};

const ItemImage = ({ item }: { item: any }) => {
    const image = getImage(item);

    return <Image alt={item.name} filename={image} className={cn("rounded-t-md object-cover ")} />;
};

const ItemFloatingLowStock = ({ item }: { item: any }) => {
    if (!item.lowStock) return null;

    return (
        <div className={cn(styles.floatingLowStock)}>
            <IoBatteryHalfOutline className="w-5 h-5" />
            <span>{item.lowStockMessage || "Estoc bajo"}</span>
        </div>
    );
};

const ItemFloatingOffer = ({ item }: { item: any }) => {
    // Si no hay stock, no mostramos la oferta
    if (!item.stock) return null;

    const offer = item.offer;
    const currentPrice = item.price;

    // Si no hay oferta, o la oferta y el precio regular es el mismo no mostramos nada
    if (!offer || offer === currentPrice || currentPrice > offer) return null;

    const dto = (1 - currentPrice / offer) * 100;

    return (
        <div className={cn(styles.floatingOffer)}>
            <MdOutlineLocalOffer className="w-4 h-4" />
            <span>-{Math.round(dto)} %</span>
        </div>
    );
};

const ItemRef = ({ id }: { id: number }) => {
    return <div className="text-right text-xxs text-ccyan uppercase font-medium">Ref: {id}</div>;
};

const ItemTitle = ({ item }: { item: any }) => {
    return <h1 className={cn(styles.title)}>{item.title}</h1>;
};

const ItemStock = ({ item }: { item: any }) => {
    if (item.stock) return null;

    return (
        <div className={styles.itemStock}>
            <IoWarningOutline className="w-5 h-5" />
            <span>{item.stock_message || "No disponible"}</span>
        </div>
    );
};

const ItemPrice = ({ item, state }: { item: any; state: State }) => {
    // Si no hay stock o el producto tiene variantes, no mostramos el precio
    if (!item.stock) return null;

    // Buscar el objeto completo para stateVariant
    const stateVariant = state.selectedVariants
        .map((selectedVariant: SelectVariant) =>
            state.allItems.find((item: any) => item.id === selectedVariant.child && item.enabled),
        )
        .find((p: any) => p && p.parent === item.id);

    // Si hay variantes, mostramos el selector de variantes
    const data = stateVariant || item;

    const offer = data.offer;
    const currentPrice = data.price;
    const offerText = offer && data.offer_text;

    const isBook = item.type === "book";
    const isApp = item?.app;
    const units = item?.items || 1;
    const priceText = data.price_text || data.split_payment;
    const isOffer = offer && offer !== currentPrice && currentPrice < offer;

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-center gap-2">
                {isOffer && (
                    <span className="line-through text-gray-500">
                        {offer.toLocaleString("es-ES", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}
                    </span>
                )}

                <span className="text-green-700 font-semibold">
                    {currentPrice.toLocaleString("es-ES", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}{" "}
                    €
                </span>

                {/**Gastos de envío */}
                {isBook && (
                    <>
                        <span>+</span>
                        <GoPackageDependents
                            className="w-5 h-5 text-ccyan-dark flex-shrink-0"
                            title="Más gastos de envío"
                        />
                    </>
                )}

                {(isBook || isApp) && <span>=</span>}

                {/** Renderizamos los ítems libros */}
                <div className="flex items-center -space-x-2">
                    {isBook &&
                        units >= 1 &&
                        Array.from({ length: Math.min(units, 3) }).map((_, i) => (
                            <GiBlackBook
                                key={`giblackbook-${i}`}
                                className="w-5 h-5 text-ccyan-dark flex-shrink-0"
                                title={`Incluye ${units} libro/s`}
                            />
                        ))}
                </div>

                {/** Renderizamos los ítems app */}
                {isApp && (
                    <>
                        <span>+</span>
                        <GiSmartphone
                            className="w-5 h-5 text-ccyan-dark flex-shrink-0"
                            title="Incluye módulo de la aplicación móvil"
                        />
                    </>
                )}
            </div>

            {/** En caso que haya texto de oferta */}
            {isOffer && offerText && (
                <div className="flex items-center justify-center gap-2 rounded-full bg-red-300 py-2 px-5 text-red-800 text-xs mx-auto">
                    <MdOutlineLocalOffer className="w-5 h-5 flex-shrink-0" />
                    <span>{offerText}</span>
                </div>
            )}

            {/** Si hay un texto del precio*/}
            {priceText && (
                <div className="flex items-center justify-center gap-2 rounded-full bg-yellow-300 py-2 px-5 text-yellow-800 text-xs mx-auto">
                    <MdEuroSymbol className="w-5 h-5 flex-shrink-0" />
                    <span>{priceText}</span>
                </div>
            )}
        </div>
    );
};

const ItemButtons = ({ item, state }: { item: any; state: State }) => {
    const [play] = useSound(addToBasketSfx, { volume: 0.25 });

    // @ts-ignore
    const { addProduct } = useContext(CartContext);
    const specialBuy = item.special_buy;

    const handleAddToCart = () => {
        // Miramos si el item.id tiene un variant
        // Buscar el objeto completo para stateVariant
        const stateVariant = state.selectedVariants
            .map((selectedVariant: SelectVariant) =>
                state.allItems.find(
                    (item: any) => item.id === selectedVariant.child && item.enabled,
                ),
            )
            .find((p: any) => p && p.parent === item.id);

        // Si no hay variantes, añadimos el item tal cual al carrito
        const product = stateVariant || item;
        addProduct(product);

        // Reproducimos el sonido de añadir al carrito
        play();

        // Añadimos el item al carrito
        toast.success("Producto añadido al carrito", {
            position: "bottom-center",
            closeOnClick: true,
            pauseOnHover: false,
            autoClose: 3000,
        });
    };

    return (
        <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-5 mt-auto")}>
            {!specialBuy ? (
                <button
                    disabled={!item.stock}
                    className={cn(
                        styles.button,
                        "text-cpurple duration-300 border-cpurple hover:bg-cpurple text-sm",
                        !item.stock && "opacity-25 pointer-events-none",
                    )}
                    onClick={handleAddToCart}
                >
                    <MdAddShoppingCart className="w-6 h-6" />
                    <span className="uppercase text-xs font-medium">Comprar</span>
                </button>
            ) : (
                <Link
                    to={specialBuy}
                    className={cn(
                        styles.button,
                        "text-cpurple duration-300 border-cpurple hover:bg-cpurple text-sm",
                        !item.stock && "opacity-25 pointer-events-none",
                    )}
                >
                    <MdAddShoppingCart className="w-6 h-6" />
                    <span className="uppercase text-xs font-medium">Comprar</span>
                </Link>
            )}

            <Link
                to={item.link}
                className={cn(
                    styles.button,
                    "hover:text-white text-ccyan-dark border-ccyan-dark hover:bg-ccyan-dark",
                    (!item.more_info || !item.link) && "opacity-25 pointer-events-none",
                )}
            >
                <MdInfoOutline className="w-6 h-6" />
                <span className="uppercase text-xs font-medium">
                    <span className="inline md:hidden">Más información</span>
                    <span className="hidden md:inline">Más info</span>
                </span>
            </Link>
        </div>
    );
};

const ItemDescription = ({ item }: { item: any }) => {
    if (!item.description) return null;

    return <div className={styles.description}>{item.description}</div>;
};

const ItemMultipleLangs = ({ item }: { item: any }) => {
    if (!item.multiple_languages) return null;

    return (
        <FaEarthAsia className="w-4 h-4 text-cpurple" title="Curso disponible en varios idiomas" />
    );
};

interface ItemVariantsProps {
    item: any;
    state: State;
    setState: Dispatch<SetStateAction<State>>;
}

const ItemVariants = ({ item, state, setState }: ItemVariantsProps) => {
    // Memoriza los childs o variantes para evitar cambios constantes en las dependencias
    const variants = useMemo(
        () => state.allItems.filter(i => item.childs?.includes(i.id) && i.enabled),
        [state.allItems, item.childs],
    );

    if (item.id === 650) {
        console.log(variants, item.childs, item.childs?.includes(651));
    }

    // Si no hay variantes, no mostramos nada
    if (!variants.length) return null;

    // Buscar el objeto completo para stateVariant
    const stateVariant = state.selectedVariants
        .map((selectedVariant: SelectVariant) =>
            state.allItems.find((item: any) => item.id === selectedVariant.child && item.enabled),
        )
        .find((p: any) => p && p.parent === item.id);

    // Si hay variantes, mostramos el selector de variantes
    const defaultVariant =
        variants && variants.length > 0
            ? stateVariant || variants.find((p: any) => p.default_in_parent) || variants[0]
            : item;

    // @ts-ignore
    const { cartItems } = useContext(CartContext);

    // Efecto para inicializar selectedVariants con el valor predeterminado
    useEffect(() => {
        if (!state.selectedVariants.some(v => v.parent === item.id)) {
            setState(state => ({
                ...state,
                selectedVariants: [
                    ...state.selectedVariants,
                    {
                        parent: item.id,
                        child: defaultVariant.id,
                    },
                ],
            }));
        }
    }, [item.id, defaultVariant.id, setState, state.selectedVariants]);

    const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedVariant = variants.find(i => i.id === parseInt(e.target.value));

        // Si no hay variante seleccionada, no hacemos nada
        if (!selectedVariant) return;

        setState(state => ({
            ...state,

            // Añadimos la variante seleccionada
            selectedVariants: [
                // Filtramos las variantes seleccionadas previamente para el mismo item
                ...state.selectedVariants.filter(i => i.parent !== item.id),
                {
                    parent: item.id,
                    child: selectedVariant.id,
                },
            ],
        }));
    };

    return (
        <div
            className={cn(
                "flex flex-col gap-2 pt-2",
                // state.viewStyle === "twoColumns" && "lg:flex-row",
            )}
        >
            <label className="text-xs text-center text-teal-700" htmlFor="variants">
                Elige una opción del producto:
            </label>

            <select
                id="variants"
                className={styles.selectVariant}
                value={defaultVariant.id}
                onChange={handleSelect}
            >
                {variants.map((variant: any) => {
                    // Comprobamos si el item ya está en el carrito
                    const exist = cartItems.some((i: any) => i.id === variant.id);

                    const price = variant.offer || variant.price;
                    const text = `${variant.short || variant.name || variant.title} - ${price.toFixed(2)} €`;

                    return (
                        <option
                            key={variant.id}
                            value={variant.id}
                            className={cn(exist && "text-cpurple")}
                        >
                            {text}
                        </option>
                    );
                })}
            </select>
        </div>
    );
};

const getImage = (item: any) => {
    // Obtenemos la imagen por defecto del producto
    const images = item.images || [];

    // Si no hay imágenes, usamos una por defecto
    const defaultImage = images.length > 0 ? images[0] : "products/common/0";

    return defaultImage;
};

const styles = {
    container: "flex flex-col shadow hover:shadow-lg duration-300 ",
    itemStock:
        "flex items-center justify-center gap-2 rounded-full bg-purple-200 py-2 px-5 text-purple-800 text-xs",
    floatingOffer:
        "absolute top-0 left-0 rounded-tl bg-red-300 px-3 py-1 text-xs z-10 rounded-tr-sm rounded-br-sm flex flex-row gap-2 items-center text-red-800 font-medium",
    floatingLowStock:
        "absolute top-0 right-0 rounded-tr bg-purple-300 px-3 py-1 text-xs z-10 rounded-tl-sm rounded-bl-sm flex flex-row gap-2 items-center text-purple-800 font-medium",
    title: "text-balance font-semibold uppercase text-center text-sm mx-auto text-ccyan",
    imageContainer: "relative flex-shrink-0 w-full bg-gray-50 rounded-t-md",
    button: "flex justify-center gap-3 items-center rounded border-2 p-2 hover:text-white duration-300",
    description: "text-gray-500 text-sm text-center text-balance mx-auto",
    priceText: "text-xs text-red-500",
    selectVariant:
        "rounded-md border py-2 px-3 bg-white text-xs uppercase overflow-hidden text-center w-full text-gray-700 font-montserrat shadow-inner",
};

export default Component;
